import { useCallback, useContext, useEffect, useRef } from 'react';
import { areEqualNonMultiEnumPropertyValues, areEqualPropertyValues } from '../../v2/utils/areEqualPropertyValues';
import { useGetAdditionalPropertyValue } from '../../v2/hooks/useGetAdditionalPropertyValue';
import { useObjectId } from '../../v2/hooks/useObjectId';
import { useObjectTypeId } from '../../v2/hooks/useObjectTypeId';
import { useProperty } from '../../v2/hooks/useProperty';
import { usePropertyValue } from '../../v2/hooks/usePropertyValue';
import { useIdentifierContext } from '../../v2/context/IdentifierContext';
import { AutosaveContext } from '../context/AutosaveContext';
import { useHighlySensitivePropertyValues } from '../../dataSensitivity/useHighlySensitivePropertyValues';
import { getOwnProperty } from '../../utils/getOwnProperty';
const DEFAULT_HIGHLY_SENSITIVE_VALUES = {};
export const usePropertyEdits = ({
  onChange
}) => {
  const {
    inputId
  } = useIdentifierContext();
  const autosaveContext = useContext(AutosaveContext);
  const property = useProperty();
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const propertyValue = usePropertyValue();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const pendingEditsRef = useRef({});
  const originalValues = useRef({});
  const highlySensitiveValues = useHighlySensitivePropertyValues({
    objectTypeId,
    objectId,
    defaultValues: DEFAULT_HIGHLY_SENSITIVE_VALUES
  });
  const clearEdits = useCallback(() => {
    pendingEditsRef.current = {};
    originalValues.current = {};
  }, []);
  const getHasPendingEdits = useCallback(() => {
    return Object.keys(pendingEditsRef.current).length > 0;
  }, []);
  const getPendingEdits = useCallback(() => {
    return pendingEditsRef.current;
  }, []);
  const getOriginalValues = useCallback(() => {
    return originalValues.current;
  }, []);
  const wrappedOnChange = useCallback(changes => {
    Object.entries(changes).forEach(([name, value]) => {
      pendingEditsRef.current[name] = value;
      if (!Object.prototype.hasOwnProperty.call(originalValues.current, name)) {
        const originalValue = getAdditionalPropertyValue(name, objectId, objectTypeId);

        // A bit of a hack here: `getAdditionalPropertyValue` does not properly include highly sensitive values.
        // This does not come up usually, since highly sensitive properties cannot be used in most features that would
        // require lookup via `getAdditionalPropertyValue`.
        //
        // Highly sensitive properties are often "empty" to begin with, because the API will return undefined (or null for GQL)
        // which is used until the user decrypts the value. So, we check if the value we lookup up is empty, but the sensitive values
        // provider has a meaningful value stored for the property.
        const highlySensitiveValue = getOwnProperty(highlySensitiveValues, name);
        if ((originalValue === undefined || originalValue === null) && highlySensitiveValue !== undefined) {
          originalValues.current[name] = highlySensitiveValue;
        } else {
          originalValues.current[name] = originalValue;
        }
      }
    });
    if (Object.keys(changes).length > 0) {
      var _autosaveContext$hasR;
      Object.entries(changes).forEach(([name, value]) => {
        // We don't have access to all property definitions and the FDSR cache was
        // found to not be warm here, so this comparison may return a false negative.
        // That is okay and only means we will save a property value to what it previously was.
        const hasReturnedToOriginal = areEqualNonMultiEnumPropertyValues(value, originalValues.current[name]);
        if (hasReturnedToOriginal) {
          delete pendingEditsRef.current[name];
        }
      });
      if (autosaveContext !== null && autosaveContext !== void 0 && (_autosaveContext$hasR = autosaveContext.hasRequestForSource) !== null && _autosaveContext$hasR !== void 0 && _autosaveContext$hasR.call(autosaveContext, inputId)) {
        autosaveContext.cancelRequestsForSource(inputId);
      }
    }
    onChange(changes);
  }, [objectTypeId, onChange, getAdditionalPropertyValue, objectId, autosaveContext, inputId, highlySensitiveValues]);
  useEffect(() => {
    // If the host gives us a new value that is different from the changes we are tracking,
    // assume that it means the value has been saved elsewhere and updated here.
    const pendingEdit = pendingEditsRef.current[property.name];
    const currentValue = propertyValue;
    if (!areEqualPropertyValues(pendingEdit, currentValue, property)) {
      var _autosaveContext$hasR2;
      clearEdits();
      if (autosaveContext !== null && autosaveContext !== void 0 && (_autosaveContext$hasR2 = autosaveContext.hasRequestForSource) !== null && _autosaveContext$hasR2 !== void 0 && _autosaveContext$hasR2.call(autosaveContext, inputId)) {
        autosaveContext.cancelRequestsForSource(inputId);
      }
    }
  }, [propertyValue, clearEdits, property, autosaveContext, inputId]);
  return {
    clearEdits,
    getPendingEdits,
    getOriginalValues,
    getHasPendingEdits,
    onChange: wrappedOnChange
  };
};